/**
 * Scrubbing PII pour les events Sentry — invariant 4.A.15 du REFACTOR_PLAN.
 *
 * Couvre :
 *   - PAN (13-19 chiffres consécutifs)
 *   - CVV (3-4 chiffres après "cvv"/"cvc")
 *   - JWT (chaînes base64 commençant par "eyJ")
 *   - Champs nommés `password`, `pwd`, `secret`, `token`, `apikey`, `api_key`
 *   - Email
 *
 * S'applique récursivement à tout objet/string passé.
 *
 * Tags AUTORISÉS (non scrubbés) — ces clés sont des identifiants techniques
 * sans PII et sont précieux pour le debug :
 *   - tenant_id, request_id, sale_id, user_id (UUID, pas PII)
 */

const REDACTED = '[REDACTED]';
const PAN_REPLACEMENT = '[PAN_REDACTED]';
const JWT_REPLACEMENT = '[JWT_REDACTED]';
const EMAIL_REPLACEMENT = '[EMAIL_REDACTED]';

// PAN : 13 à 19 chiffres consécutifs (avec ou sans séparateurs)
// On masque le PAN complet, mais on garde les 4 derniers chiffres pour debug
const PAN_REGEX = /\b(\d[\s-]?){12,18}\d\b/g;

// JWT : signature base64 typique commence par "eyJ" (header `{"alg":...}` encoded)
// Format : 3 segments base64url séparés par "."
const JWT_REGEX = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;

// Email basique (RFC simplifié — préfère faux positifs à laisser passer un email)
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

// Champs sensibles par nom (case-insensitive)
const SENSITIVE_FIELD_NAMES = new Set([
  'password',
  'passwd',
  'pwd',
  'secret',
  'token',
  'auth',
  'bearer',
  'authorization',
  'apikey',
  'api_key',
  'access_token',
  'refresh_token',
  'session_token',
  'private_key',
  'cvv',
  'cvc',
  'cvv2',
  'card_security_code',
  'pan',
  'card_number',
  'cardnumber',
  'track1',
  'track2',
  'magnetictrack',
  'pin',
  'pin_code',
  // PII contact (RGPD)
  'email',
  'phone',
  'phone_number',
  'mobile',
  'tel',
]);

// Champs explicitement autorisés (non scrubbés même si nom suspect)
const ALLOWLIST_FIELD_NAMES = new Set([
  'tenant_id',
  'request_id',
  'sale_id',
  'user_id',
  'tenant_slug',
  'register_id',
  'reference',
  'event_type',
  // last4 = 4 derniers chiffres carte = autorisé (pas un PAN complet)
  'last4',
  'last_4',
  'card_last4',
  // pan_seq = numéro séquence PAN, pas le PAN
  'pan_seq',
]);

/**
 * Masque un PAN en gardant uniquement les 4 derniers chiffres (norme PCI DSS).
 */
function maskPan(match: string): string {
  const digitsOnly = match.replace(/[\s-]/g, '');
  if (digitsOnly.length < 13) return match;
  const last4 = digitsOnly.slice(-4);
  return `${PAN_REPLACEMENT}${last4}`;
}

/**
 * Scrub une string : remplace PAN, JWT, email par leurs placeholders.
 */
export function scrubString(input: string): string {
  if (typeof input !== 'string') return input;
  return input
    .replace(PAN_REGEX, (m) => maskPan(m))
    .replace(JWT_REGEX, JWT_REPLACEMENT)
    .replace(EMAIL_REGEX, EMAIL_REPLACEMENT);
}

/**
 * Scrub récursif d'un objet : remplace les valeurs des champs sensibles
 * par REDACTED, et applique scrubString à toutes les strings.
 */
export function scrubObject<T>(input: T, depth = 0): T {
  if (depth > 20) return input; // Garde-fou anti-cycle
  if (input == null) return input;

  if (typeof input === 'string') {
    return scrubString(input) as unknown as T;
  }

  if (Array.isArray(input)) {
    return input.map((item) => scrubObject(item, depth + 1)) as unknown as T;
  }

  if (typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
      const lowerKey = key.toLowerCase();

      if (ALLOWLIST_FIELD_NAMES.has(lowerKey)) {
        out[key] = value;
        continue;
      }

      if (SENSITIVE_FIELD_NAMES.has(lowerKey)) {
        out[key] = REDACTED;
        continue;
      }

      out[key] = scrubObject(value, depth + 1);
    }
    return out as T;
  }

  return input;
}

/**
 * beforeSend Sentry — applique le scrubbing à l'event complet avant envoi.
 *
 * Couvre :
 *   - event.message
 *   - event.exception.values[].value et stacktrace
 *   - event.breadcrumbs[].message + data
 *   - event.extra
 *   - event.contexts
 *   - event.request (URL, headers, data)
 *   - event.user (retire email)
 */
export function scrubSentryEvent<T extends object>(event: T): T {
  return scrubObject(event as T & Record<string, unknown>) as T;
}
